// Quill inline format for links between notes ("[[" picker).
//
// Saved HTML: <a href="/notes/<id>" data-note-link="<id>" class="note-link">First line</a>
// The format value is the target note id. It extends Quill's Link so the
// snow theme and clipboard treat it as a link, but it is matched by its class
// (Parchment checks classes before tag names), so plain <a> links still load
// as ordinary links.
//
// Registration takes the Quill constructor: the app passes react-quill-new's
// (see utils/ensureNoteLink.js), Jest passes the UMD build.

export const NOTE_LINK_FORMAT = "notelink";
export const NOTE_LINK_CLASS = "note-link";
export const NOTE_LINK_MISSING_CLASS = "note-link--missing";
export const NOTE_LINK_SELECTOR = "a[data-note-link]";

const OBJECT_ID = /^[0-9a-f]{24}$/i;

export const isNoteId = (value) => typeof value === "string" && OBJECT_ID.test(value);

export const noteLinkPath = (id) => `/notes/${id}`;

export const registerNoteLink = (Quill) => {
  if (!Quill || typeof Quill.import !== "function") return null;
  const existing = Quill.imports && Quill.imports[`formats/${NOTE_LINK_FORMAT}`];
  if (existing) return existing;
  const Link = Quill.import("formats/link");

  class NoteLink extends Link {
    static blotName = NOTE_LINK_FORMAT;
    static tagName = "A";
    static className = NOTE_LINK_CLASS;

    static create(value) {
      const id = isNoteId(value) ? value : "";
      const node = super.create(noteLinkPath(id));
      // In-app link: same tab, no opener stripping needed.
      node.removeAttribute("target");
      node.removeAttribute("rel");
      node.setAttribute("href", noteLinkPath(id));
      node.setAttribute("data-note-link", id);
      return node;
    }

    static formats(domNode) {
      const id = domNode.getAttribute("data-note-link");
      return isNoteId(id) ? id : undefined;
    }

    format(name, value) {
      if (name !== this.statics.blotName || !value) {
        super.format(name, value);
      } else if (isNoteId(value)) {
        this.domNode.setAttribute("href", noteLinkPath(value));
        this.domNode.setAttribute("data-note-link", value);
      }
    }
  }

  Quill.register(NoteLink, true);
  return NoteLink;
};

// Replaces `length` chars at `index` (the "[[query" token, already deleted by
// the picker when length is 0) with a link to `note`, then a plain space so
// typing continues outside the link. Goes through Quill with source "user"
// so autosave and collaboration see it.
export const insertNoteLink = (quill, index, note) => {
  if (!quill || !note || !isNoteId(note.id)) return;
  const title = String(note.title || "").trim() || "Untitled note";
  const Delta = quill.constructor.import("delta");
  quill.updateContents(
    new Delta().retain(index).insert(title, { [NOTE_LINK_FORMAT]: note.id }).insert(" "),
    "user"
  );
  quill.setSelection(index + title.length + 1, 0, "silent");
};
